import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { usePublicFetch } from './usePublicFetch';
import { useOTPAuth } from '../auth/providers/OTPAuthProvider';
import { useCurrentUser } from './useApi';
import { API_URL } from '../utils/config';
import type {
  Idea,
  ArchivedIdea,
  FeatureUpdate,
  SubmitIdeaRequest,
  SubmitIdeaResponse,
} from '../types/ideaBoard';

const IDEA_BOARD_BASE = `${API_URL}/api/store/idea-board`;

/**
 * Returns a stable anonymous identifier for the current user/browser session.
 * Stored in localStorage so it persists across page loads.
 */
function getUserIdentifier(): string {
  const key = 'idea_board_user_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

// ---------------------------------------------------------------------------
// GET active ideas
// ---------------------------------------------------------------------------
export const useIdeas = () => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { publicFetch } = usePublicFetch();
  const { data: user } = useCurrentUser();
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery<Idea[], Error>({
    queryKey: ['ideaBoard', 'ideas'],
    queryFn: async (): Promise<Idea[]> => {
      const url = `${IDEA_BOARD_BASE}/ideas/list?status=active`;
      const userIdentifier = isAuthenticated && user?.email ? user.email : getUserIdentifier();

      let response: Response | null;
      if (isAuthenticated) {
        response = await authenticatedFetch(url, {
          headers: { 'X-User-Identifier': userIdentifier },
        });
      } else {
        response = await publicFetch(url, {
          headers: { 'X-User-Identifier': userIdentifier },
        });
      }

      if (!response) {
        console.error('Failed to fetch ideas: No response');
        return [];
      }
      if (!response.ok) {
        console.error(`Failed to fetch ideas: HTTP ${response.status} ${response.statusText}`);
        return [];
      }

      const result = await response.json();
      return result.data?.ideas || [];
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// ---------------------------------------------------------------------------
// GET archived ideas
// ---------------------------------------------------------------------------
export const useArchivedIdeas = () => {
  const { publicFetch } = usePublicFetch();

  return useQuery<ArchivedIdea[], Error>({
    queryKey: ['ideaBoard', 'archived'],
    queryFn: async (): Promise<ArchivedIdea[]> => {
      const url = `${IDEA_BOARD_BASE}/ideas/archived`;
      const response = await publicFetch(url);

      if (!response) {
        console.error('Failed to fetch archived ideas: No response');
        return [];
      }
      if (!response.ok) {
        console.error(`Failed to fetch archived ideas: HTTP ${response.status} ${response.statusText}`);
        return [];
      }

      const result = await response.json();
      return result.data?.archivedIdeas || [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

// ---------------------------------------------------------------------------
// GET feature updates
// ---------------------------------------------------------------------------
export const useFeatureUpdates = (limit: number = 20) => {
  const { publicFetch } = usePublicFetch();

  return useQuery<FeatureUpdate[], Error>({
    queryKey: ['ideaBoard', 'featureUpdates', limit],
    queryFn: async (): Promise<FeatureUpdate[]> => {
      const url = `${IDEA_BOARD_BASE}/feature-updates/list?limit=${limit}`;
      const response = await publicFetch(url);

      if (!response) {
        console.error('Failed to fetch feature updates: No response');
        return [];
      }
      if (!response.ok) {
        console.error(`Failed to fetch feature updates: HTTP ${response.status} ${response.statusText}`);
        return [];
      }

      const result = await response.json();
      return result.data?.featureUpdates || [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

// ---------------------------------------------------------------------------
// POST submit a new idea
// ---------------------------------------------------------------------------
export const useSubmitIdea = () => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { publicFetch } = usePublicFetch();
  const queryClient = useQueryClient();
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useMutation<SubmitIdeaResponse, Error, SubmitIdeaRequest>({
    mutationFn: async (body: SubmitIdeaRequest): Promise<SubmitIdeaResponse> => {
      const url = `${IDEA_BOARD_BASE}/ideas/submit`;
      const options = {
        method: 'POST',
        body: JSON.stringify(body),
      };

      let response: Response | null;
      if (isAuthenticated) {
        response = await authenticatedFetch(url, options);
      } else {
        response = await publicFetch(url, options);
      }

      if (!response) {
        throw new Error('Authentication failed or no response from server.');
      }
      if (!response.ok) {
        const errorText = await response.text().catch(() => response!.statusText);
        throw new Error(`Failed to submit idea: HTTP ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ideaBoard', 'ideas'] });
    },
  });
};

// ---------------------------------------------------------------------------
// POST like an idea (with optimistic update)
// ---------------------------------------------------------------------------
export const useLikeIdea = () => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { publicFetch } = usePublicFetch();
  const { data: user } = useCurrentUser();
  const queryClient = useQueryClient();
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useMutation<{ ideaId: number; likeCount: number; userHasLiked: boolean }, Error, number>({
    mutationFn: async (ideaId: number) => {
      const url = `${IDEA_BOARD_BASE}/ideas/${ideaId}/like`;
      const userIdentifier = isAuthenticated && user?.email ? user.email : getUserIdentifier();
      const options = {
        method: 'POST',
        headers: { 'X-User-Identifier': userIdentifier },
      };

      let response: Response | null;
      if (isAuthenticated) {
        response = await authenticatedFetch(url, options);
      } else {
        response = await publicFetch(url, options);
      }

      if (!response) {
        throw new Error('Authentication failed or no response from server.');
      }
      if (!response.ok) {
        const errorText = await response.text().catch(() => response!.statusText);
        throw new Error(`Failed to like idea: HTTP ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      return result.data;
    },
    onMutate: async (ideaId: number) => {
      // Cancel in-flight queries
      await queryClient.cancelQueries({ queryKey: ['ideaBoard', 'ideas'] });

      // Snapshot current data for rollback
      const previousIdeas = queryClient.getQueryData<Idea[]>(['ideaBoard', 'ideas']);

      // Optimistically update
      queryClient.setQueryData<Idea[]>(['ideaBoard', 'ideas'], (old) => {
        if (!old) return old;
        return old.map((idea) =>
          idea.id === ideaId
            ? { ...idea, likeCount: idea.likeCount + 1, userHasLiked: true }
            : idea
        );
      });

      return { previousIdeas };
    },
    onError: (_err, _ideaId, context: any) => {
      // Roll back on error
      if (context?.previousIdeas) {
        queryClient.setQueryData(['ideaBoard', 'ideas'], context.previousIdeas);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['ideaBoard', 'ideas'] });
    },
  });
};

// ---------------------------------------------------------------------------
// PATCH mark idea as dealt with (admin only)
// ---------------------------------------------------------------------------
export const useMarkIdeaDealtWith = () => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { ideaId: number; featureUpdateSummary?: string }>({
    mutationFn: async ({ ideaId, featureUpdateSummary }): Promise<void> => {
      const url = `${IDEA_BOARD_BASE}/ideas/${ideaId}/deal-with`;
      const response = await authenticatedFetch(url, {
        method: 'PATCH',
        body: JSON.stringify({ featureUpdateSummary }),
      });

      if (!response) {
        throw new Error('Authentication failed.');
      }
      if (!response.ok) {
        const errorText = await response.text().catch(() => response!.statusText);
        throw new Error(`Failed to mark idea as dealt with: HTTP ${response.status} - ${errorText}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ideaBoard', 'ideas'] });
      queryClient.invalidateQueries({ queryKey: ['ideaBoard', 'archived'] });
      queryClient.invalidateQueries({ queryKey: ['ideaBoard', 'featureUpdates'] });
    },
  });
};

// ---------------------------------------------------------------------------
// POST create a feature update (admin only)
// ---------------------------------------------------------------------------
export const useCreateFeatureUpdate = () => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<FeatureUpdate, Error, { summary: string }>({
    mutationFn: async ({ summary }): Promise<FeatureUpdate> => {
      const url = `${IDEA_BOARD_BASE}/feature-updates/create`;
      const response = await authenticatedFetch(url, {
        method: 'POST',
        body: JSON.stringify({ summary }),
      });

      if (!response) {
        throw new Error('Authentication failed.');
      }
      if (!response.ok) {
        const errorText = await response.text().catch(() => response!.statusText);
        throw new Error(`Failed to create feature update: HTTP ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ideaBoard', 'featureUpdates'] });
    },
  });
};
